import { SafeStorage } from './safe-storage';

/**
 * Where a sessionless visitor was headed, so signing in can take them there
 * instead of /home.
 *
 * picaggo.com's "Pay once" buttons open /choose-subscription (optionally with
 * ?plan=standard). A visitor without a session is bounced to the sign-in front
 * door (auth-landing.ts), and before this nothing remembered why they came:
 * after signing up they landed on their events and never saw the plans.
 *
 * Two limits keep this from sending anyone somewhere they didn't ask to go:
 *
 *   - Only pages people are linked to from outside the app (isReturnable): the
 *     plans page, and a guest invite (/guest/TOKEN), whose "Sign in" hands its
 *     own address over as ?returnUrl= (rememberReturn).
 *   - For a bounce, only the page the visitor *arrived* on. ARRIVAL_PATH is the
 *     hash when this module first loads, during bootstrap, before any
 *     navigation. A bounce later in the visit — signing out while on the plans
 *     page — doesn't match it, so the next sign-in doesn't drag them back.
 *
 * Stored in localStorage rather than memory because sign-in can replace the
 * document (redirect fallback, or an iOS tab discarded while the visitor is in
 * the provider's UI), and dropped after MAX_AGE_MS so a visit abandoned today
 * doesn't decide where a sign-in next week lands.
 */
const KEY = 'postAuthReturn';
const MAX_AGE_MS = 30 * 60 * 1000;
const RETURNABLE = ['/choose-subscription', '/choose-subscription-v2'];

const ARRIVAL_PATH = currentHashPath();

function currentHashPath(): string {
  try {
    return (window.location.hash || '').replace(/^#/, '');
  } catch {
    return '';
  }
}

/** A path inside this app, on the allowlist. Never another origin. */
function isReturnable(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  const route = path.split('?')[0].replace(/\/+$/, '');
  return RETURNABLE.includes(route) || /^\/guest\/[A-Za-z0-9_-]+$/.test(route);
}

/** Called just before a sessionless visitor is bounced to sign in. */
export function rememberArrivalForAfterSignIn(): void {
  const path = currentHashPath();
  if (path !== ARRIVAL_PATH) return;
  rememberReturn(path);
}

/** An explicit hand-off (?returnUrl=). Ignored unless it is returnable. */
export function rememberReturn(path: string | null | undefined): void {
  if (!path || !isReturnable(path)) return;
  SafeStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
}

/** The remembered page, without using it up. */
export function pendingReturn(): string | null {
  const raw = SafeStorage.getItem(KEY);
  if (!raw) return null;
  try {
    const { path, at } = JSON.parse(raw);
    if (typeof path === 'string' && isReturnable(path) && Date.now() - at < MAX_AGE_MS) {
      return path;
    }
  } catch {
    // Unreadable: treat as absent.
  }
  SafeStorage.removeItem(KEY);
  return null;
}

/** The remembered page, used up: a second sign-in lands on /home as usual. */
export function takeReturn(): string | null {
  const path = pendingReturn();
  SafeStorage.removeItem(KEY);
  return path;
}

export function forgetReturn(): void {
  SafeStorage.removeItem(KEY);
}

/** For the sign-in sheet's copy: is the remembered page the plans page? */
export function isPlansPath(path: string | null): boolean {
  return !!path && path.split('?')[0].startsWith('/choose-subscription');
}
